import { ListChecks, Search, FileText, Landmark, History } from "lucide-react";
import libFloat from "../../../util/libFloat";
import { useRemessaBoleto } from "./hooks";
import Aba from "./_components/aba";
import FiltroBusca from "./_components/filtro-busca";
import TabelaBusca from "./_components/tabela-busca";
import TabelaSelecionadas from "./_components/tabela-selecionadas";
import ResultadoRemessa from "./_components/resultado-remessa";
import TabelaRemessasEmitidas from "./_components/tabela-remessas-emitidas";

/**
 * Geracao de arquivo de remessa CNAB.
 *
 * Duas abas, no mesmo padrao do modal de vincular produtos:
 * a selecao vira uma lista propria, entao ela nao se perde quando
 * o usuario navega entre paginas da busca.
 */
export default function MainRemessaBoleto() {
	const {
		activeTab,
		setActiveTab,
		parcelas,
		totalValor,
		adicionar,
		remover,
		limparSelecao,
		jaSelecionada,
		dtIni,
		setDtIni,
		dtFim,
		setDtFim,
		nomeCliente,
		setNomeCliente,
		idVenda,
		setIdVenda,
		resultado,
		jaEmitidos,
		pagina,
		totalPaginas,
		carregando,
		erro,
		buscou,
		buscar,
		convenios,
		convenioSelecionado,
		idBoletoConfig,
		setIdBoletoConfig,
		gerando,
		podeGerar,
		gerarRemessa,
		resultadoRemessa,
		baixarNovamente,
		novaRemessa,
		testando,
		baixarRemessaTeste,
	} = useRemessaBoleto();

	// Altura definida, nao min-h: o Main do sidebar nao tem altura, entao
	// height:100% cairia para auto e o flex-1/min-h-0 daqui para baixo
	// ficaria inerte. Com min-h a tela funciona, mas cresce junto com a
	// lista e o rodape sai da vista. Fixando a altura, a tabela vira uma
	// caixa que rola por dentro e o rodape fica sempre no lugar.
	return (
		<div className="mt-14 flex flex-col h-[calc(100vh-6.5rem)] bg-gray-100 overflow-hidden">
			{/* Cabecalho */}
			<div className="shrink-0 bg-white border-b px-6 py-4">
				<div className="flex items-center gap-3">
					<FileText size={22} className="text-customBlue-primary" />
					<div>
						<h1 className="text-lg font-semibold text-gray-800">Remessa de Boletos</h1>
						<p className="text-sm text-gray-500">
							Selecione as parcelas e gere o arquivo para enviar ao banco
						</p>
					</div>
				</div>
			</div>

			{/* Barra do convenio. Escolher errado gera boleto na faixa de
			    Nosso Numero de outro banco e a remessa volta recusada -
			    por isso os dados da conta ficam visiveis ao lado. */}
			<div className="shrink-0 bg-white border-b px-6 py-2 flex items-center gap-3 flex-wrap">
				<Landmark size={16} className="text-gray-400 shrink-0" />
				<span className="text-sm text-gray-600 shrink-0">Convênio</span>

				<select
					value={idBoletoConfig}
					onChange={(e) => setIdBoletoConfig(Number(e.target.value))}
					disabled={convenios.length === 0}
					className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white w-56 disabled:bg-gray-100 disabled:text-gray-400"
				>
					<option value={0}>
						{convenios.length === 0 ? "Nenhum cadastrado" : "Selecione..."}
					</option>
					{convenios.map((c) => (
						<option key={c.id_boleto_config} value={c.id_boleto_config}>
							{c.descricao || `Banco ${c.banco}`}
						</option>
					))}
				</select>

				{convenioSelecionado ? (
					<span className="text-xs text-gray-500 flex items-center gap-3">
						<span>
							Banco <strong className="text-gray-700">{convenioSelecionado.banco}</strong>
						</span>
						<span className="text-gray-300">|</span>
						<span>
							Ag <strong className="text-gray-700">
								{convenioSelecionado.agencia}
								{convenioSelecionado.agencia_dv ? `-${convenioSelecionado.agencia_dv}` : ""}
							</strong>
						</span>
						<span className="text-gray-300">|</span>
						<span>
							Cc <strong className="text-gray-700">
								{convenioSelecionado.conta}
								{convenioSelecionado.conta_dv ? `-${convenioSelecionado.conta_dv}` : ""}
							</strong>
						</span>
						<span className="text-gray-300">|</span>
						<span>
							Carteira <strong className="text-gray-700">{convenioSelecionado.carteira}</strong>
						</span>
						{convenioSelecionado.ambiente === "TESTE" && (
							<span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
								TESTE
							</span>
						)}
					</span>
				) : (
					<span className="text-xs text-gray-400">
						{convenios.length === 0
							? "Cadastre um convênio em Cadastro › Financeiro"
							: "Escolha o convênio que vai gerar os boletos"}
					</span>
				)}
			</div>

			{resultadoRemessa ? (
				<main className="flex-1 flex flex-col overflow-hidden min-h-0 p-6">
					<ResultadoRemessa
						resultado={resultadoRemessa}
						onBaixarNovamente={baixarNovamente}
						onNovaRemessa={novaRemessa}
					/>
				</main>
			) : (
				<>
			{/* Abas - Segmented Control Bar */}
			<div className="shrink-0 bg-white border-b px-6 py-2.5">
				<div className="inline-flex p-1 bg-gray-100/90 rounded-xl gap-1 border border-gray-200/60 shadow-inner">
					<Aba
						ativa={activeTab === "buscar"}
						onClick={() => setActiveTab("buscar")}
						icone={<Search size={16} />}
						texto="Buscar Parcelas"
					/>
					<Aba
						ativa={activeTab === "selecionadas"}
						onClick={() => setActiveTab("selecionadas")}
						icone={<ListChecks size={16} />}
						texto="Parcelas na Remessa"
						badge={parcelas.length}
					/>
					<Aba
						ativa={activeTab === "emitidas"}
						onClick={() => setActiveTab("emitidas")}
						icone={<History size={16} />}
						texto="Remessas Emitidas"
					/>
				</div>
			</div>

			<main className="flex-1 flex flex-col overflow-hidden min-h-0 p-6">
				{/* Fora das abas de proposito: o Gerar Remessa fica no rodape e
				    e disparado de qualquer aba, entao a recusa do servidor
				    precisa aparecer em todas. whitespace-pre-line porque o
				    cadastro incompleto vem com uma linha por cliente. */}
				{erro && (
					<div className="shrink-0 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3 whitespace-pre-line">
						{erro}
					</div>
				)}

				{activeTab === "selecionadas" && (
					<TabelaSelecionadas
						parcelas={parcelas}
						totalValor={totalValor}
						onRemover={remover}
						onLimpar={limparSelecao}
						onIrParaBusca={() => setActiveTab("buscar")}
					/>
				)}

				{activeTab === "buscar" && (
					<div className="flex-1 flex flex-col min-h-0 gap-4">
						<FiltroBusca
							dtIni={dtIni}
							setDtIni={setDtIni}
							dtFim={dtFim}
							setDtFim={setDtFim}
							nomeCliente={nomeCliente}
							setNomeCliente={setNomeCliente}
							idVenda={idVenda}
							setIdVenda={setIdVenda}
							carregando={carregando}
							onBuscar={buscar}
						/>

						<TabelaBusca
							resultado={resultado}
							pagina={pagina}
							totalPaginas={totalPaginas}
							carregando={carregando}
							buscou={buscou}
							onBuscar={buscar}
							onAdicionar={adicionar}
							onRemover={remover}
							jaSelecionada={jaSelecionada}
							jaEmitidos={jaEmitidos}
						/>
					</div>
				)}

				{activeTab === "emitidas" && <TabelaRemessasEmitidas />}
			</main>
				</>
			)}

			{/* Rodape - escondido na tela de resultado, que tem os
			    proprios botoes. */}
			{!resultadoRemessa && (
			<div className="shrink-0 bg-white border-t px-6 py-3 flex items-center gap-3">
				{/* So aparece onde a remessa e montada, e depois da primeira
				    escolha: "0 parcelas | R$ 0,00" nao informa nada. */}
				{activeTab !== "emitidas" && parcelas.length > 0 && (
					<span className="text-sm text-gray-600">
						<strong className="text-gray-800">{parcelas.length}</strong>{" "}
						{parcelas.length === 1 ? "parcela" : "parcelas"}
						<span className="mx-2 text-gray-300">|</span>
						<strong className="text-gray-800">{libFloat.strToCurrency(totalValor)}</strong>
					</span>
				)}

				<div className="ml-auto flex items-center gap-3">
					{idBoletoConfig === 0 && (
						<div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md">
							<span className="flex h-2 w-2 relative">
								<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
								<span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
							</span>
							<span className="font-medium">
								{convenios.length === 0
									? "Cadastre um convênio para gerar remessas"
									: "Selecione um convênio para gerar a remessa"}
							</span>
						</div>
					)}

					<button
						onClick={gerarRemessa}
						disabled={!podeGerar}
						title={
							idBoletoConfig === 0
								? "Escolha o convênio primeiro"
								: parcelas.length === 0
									? "Adicione ao menos uma parcela"
									: "Gera o arquivo e registra os boletos"
						}
						className="px-4 py-2 rounded-md text-sm font-medium bg-customBlue-primary text-white hover:bg-customBlue-secondary disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
					>
						{gerando ? "Gerando..." : "Gerar Remessa"}
					</button>
				</div>
			</div>
			)}
		</div>
	);
}
